/**
 * Reports → Mobile Usage.
 *
 * WHO THIS PAGE IS FOR: the program team running a client project. It answers "how is my
 * ground team working" — not "is the app healthy". Every card is written for a reader who
 * has never seen an analytics tool, carries a one-line "what to do when this looks wrong",
 * and prints units on every number. The earlier engineering-facing cards (active people
 * per day as the headline, most-used screens, who-is-using-it, how long each flow takes)
 * were removed: the daily active count never moved, and the role breakdown behind
 * who-is-using-it counted some people twice.
 *
 * THE SNAPSHOT INTERFACE BELOW MIRRORS THE API, FIELD FOR FIELD. It is a copy of
 * `MobileUsageSnapshot` in apps/api/src/services/posthog-usage.service.ts: block names
 * (`perDayUse`, `dayByDay`, `timeShare`, `pageTime`, `reach`), the `group` discriminator on
 * the rows that have one, and the worklist contract where `people` is a COUNT and `rows` is
 * the array. Never invent a friendlier name here. An earlier revision did, and the result
 * was not a blank card but a confident wrong answer: the follow-up lists printed their
 * all-clear sentences ("everyone who used the app also marked attendance") over a payload
 * that was full of people to call. If the API renames a field, rename it here and let
 * MobileUsageReportPage.test.tsx fail — it feeds a fixture written in the API's shape
 * precisely so that drift cannot pass silently.
 *
 * WHAT WAS REMOVED, AND WHY IT MAY NOT COME BACK: card 6 was "Shifts that were never
 * closed" until Oct 2026. A missing close-of-day already raises a red flag and is chased
 * from the Red Flags dashboard, and two pages chasing the same people is how one of them
 * goes quietly stale. If it is wanted again, it belongs where the follow-up already happens,
 * not here.
 *
 * SO CARD 6 IS A VACANT NUMBER, deliberately not closed up — the same decision the service
 * records at the top of posthog-usage.service.ts. Renumbering would silently re-point every
 * "see card N" note in both files at a different card, and those notes are the only record
 * of why several of these cards are worded the way they are. The newest card is 12 and is
 * numbered 12 here, even though it READS best straight after card 3, which is where it is
 * rendered.
 *
 * ACCESS: the `web.mobile_usage` page right (its page’s tick) (+ super admins), enforced by
 * `requireMobileUsageAccess` on the route. The client cannot evaluate that, so a 403
 * from the snapshot call IS the access answer and renders AccessDeniedPanel rather
 * than an error banner — same contract as StartExitPage.
 *
 * LANGUAGE RULES (do not relax them): no "journey", no "schema", no "p90", no "attempt",
 * no "distinct" anywhere a user can read. A spread is shown as a range ("most are between
 * 4 and 13 min"), never as a percentile.
 *
 * SAY WHICH DAYS, NOT "THIS WEEK". The API's window is whole SETTLED IST days ending two
 * days back, because phones report usage in windows that arrive late (`window`,
 * `provisionalFrom`). So "this week" would describe a week that ended two days ago. The
 * real dates are printed once at the top and every card title says "the N complete days
 * to <date>". A manager must not ring someone about behaviour that changed yesterday.
 *
 * HONESTY OF THE NUMBERS: time on screen is timed by current app builds and, for older
 * builds still in the field, worked out from the gaps between events. The API reports the
 * measured share as `timeShareMeasuredPct` and card 3 labels itself from it,
 * UNCONDITIONALLY — a card that is only sometimes honest is not honest. Do not remove that
 * label, and do not state anywhere that the times were all measured.
 *
 * READING THE API DEFENSIVELY: every block of the snapshot is independently nullable —
 * the API degrades one card rather than the page (see `degraded`). So each card here
 * checks its own data and says it is unavailable instead of rendering a zero that reads
 * as "nobody did this".
 *
 * AND "EMPTY" IS NOT THE SAME QUESTION AS "FAILED", which is the trap that check falls
 * into. A block that threw does NOT arrive as null: the service's empty() factory leaves
 * the hours and the training rows as `[]` and every worklist as
 * `{ people: 0, rows: [], truncated: false }`, and the only record of the failure is the
 * block's key in `degraded`. A card testing emptiness alone therefore printed the banner
 * "… could not be loaded this time" directly above its own "nothing was logged in this
 * window", with the reassuring half in bigger type. Ask unreadable() first, and keep the
 * measured sentence for a block that arrived and was genuinely empty.
 *
 * WHO IS LEFT OUT OF THE FOLLOW-UP LISTS: the API drops the people 1Office says have
 * EXITED, because a leaver is never somebody to ring — the barely-using list used to be
 * topped by them. It does NOT drop a person whose status never resolved (unknown is not the
 * same as gone, and dropping an unknown would hide a real person), and it reports how many
 * it hid as `hiddenLeavers` on the list itself, which every card prints. A list that shrank
 * must never be a mystery. The "No longer with the company" label in
 * MobileUsageWorklist therefore stays exactly as it was: it is the honest answer for any row
 * that still arrives marked as an exit, and it is deliberately NOT printed for an unknown.
 * The page does no filtering of its own — only the controller knows who has left.
 *
 * CARD 12 IS THE FULL LIST BEHIND CARD 3, which summarises every page away into a dozen
 * business areas and so cannot answer "how long do people spend on daily task reporting".
 * It is ground staff only for the same reason card 3 is: five route names (Attendance,
 * Grooming, RandomAttendance, RedFlags and YellowFlags) serve both ground staff and
 * managers, so a card mixing the two could not say whose minutes it was showing. Its
 * navigation rows are flagged on the row itself, because "Home" sorts near the top on total
 * hours and must never be read as the most-used feature.
 *
 * ITS PAGE NAMES ARE THE SERVICE'S WORDS (SCREEN_LABELS in posthog-usage.service.ts), and
 * they are printed verbatim, so the LANGUAGE RULES above bind that table too. Two labels in
 * it currently read "Learning journeys" and "One learning journey" — the product's own name
 * for the feature, but also the one word this page bans. The fixture does not carry them, so
 * the banned-word test cannot see it: either the service renames those two or this page's
 * ban is narrowed to the analytics sense of the word. Until that is decided, do not add them
 * to the fixture and assume it passes.
 *
 * CARD 5 IS HIDDEN FROM EVERYONE (26 Oct 2026) — see SHOW_WHAT_STOPPED_PEOPLE below. What
 * follows describes it as written, for whoever switches it back on.
 * CARD 5'S ROWS OPEN. Each blocker carries `who`, the people it happened to, so "410 people
 * hit this" becomes a list of names without another page load. A row the API sent no people
 * for is not clickable and does not look clickable — a control that opens nothing is worse
 * than no control at all. `who` is capped and leaver-filtered, so it can be a strict subset
 * of the row's own `people`: when it is, the panel says so rather than letting the reader
 * take four names for the whole story.
 *
 * CARDS 9-11 EACH CARRY A TRUTH THAT IS EASY TO READ BACKWARDS, so none of them may be
 * shown without the sentence that goes with it:
 *   - 9, the hours of the day, is built from LOGGED WORK only (check-in, surprise check,
 *     check-out, task report). The app's own screen time arrives in windows over an hour
 *     long, so it cannot place anybody at an hour and the API deliberately does not try.
 *     The card therefore says "when work is logged", never "when the app is open". It also
 *     prints how few task reports arrive after that person's own check-out, because a
 *     21:00 peak otherwise reads as staff writing the day up from memory at home — which
 *     is not what the figures say, and is an accusation this page must never make.
 *   - 10, training, compares TOTALS and nothing else: the start and the finish carry no
 *     course id, so "553 started, 58 finished" is two totals over the same people and not
 *     58 of those 553. The two kinds of course are measured by different code — one cannot
 *     report a failure, the other cannot report a give-up — so the card says not to read
 *     them against each other, and the broken-player call comes from the API's own
 *     `finishMayNotBeReported` rather than from a rule this page invents. It points at an
 *     engineering check, never at the learners.
 *   - 11, nobody who never opens a notification can be said to have never been SENT one:
 *     nothing records a message arriving. The card is a list of phone settings to check,
 *     and it prints `noNotificationsStillAnsweredChecks` — most of these people reach the
 *     surprise check some other way — so it can never be read as "they missed their
 *     checks".
 */
import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from 'recharts';
import type { DotItemDotProps } from 'recharts';
import {
  RefreshCcw, AlertTriangle, Info, Smartphone, Settings2, ChevronDown, ChevronRight, Compass,
} from 'lucide-react';
import { mobileUsageAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { AccessDeniedPanel } from '../components/AccessDeniedPanel';
import { InlineLoader } from '../components/loading';
import { WorklistTable } from '../components/MobileUsageWorklist';
import { count, days, minutes, n, people, seconds } from '../utils/usageNumbers';
import MobileUsagePeopleModal, { type DrillRequest } from '../components/MobileUsagePeopleModal';
import type { UsagePerson } from '../components/MobileUsageWorklist';

// Chart marks. The brand navy (#3D5A80) is too low-chroma to work as a data colour —
// it reads as grey against the white card — so marks use a saturated sibling from the
// same family. The pair below is checked for colour-blind separation and 3:1 contrast
// on white; changing either hex should be re-checked, not eyeballed.
/** "What stopped people" is built and tested but shown to nobody (26 Oct 2026): this page
 *  is about how the app is used, and who should see failure data is a separate decision.
 *  Set this true AND take 'blockers' out of HIDDEN_BLOCKS in posthog-usage.service.ts to
 *  bring the card back exactly as it was. */
const SHOW_WHAT_STOPPED_PEOPLE = false;

/**
 * The three standing worklists — "Marking attendance some other way", "Barely using the
 * app", "Never sees the app's notifications" — are built and tested but shown to nobody
 * (27 Oct 2026).
 *
 * WHY, now and not before: until the drill-downs landed, a named list was the only way to
 * answer "which people?". Now every figure on the page opens its own people, so the
 * question is answered where it is asked. What is left standing on the page without that
 * excuse is a roll-call of the people using the app least — which is the part of this page
 * a client should not be shown, and which the owner wants to decide the audience for
 * separately.
 *
 * Set this true AND take the three keys out of HIDDEN_BLOCKS in posthog-usage.service.ts to
 * bring all three cards back exactly as they were. While the keys are in there the queries
 * never run, and the snapshot stops resolving names altogether.
 */
const SHOW_PEOPLE_WORKLISTS = false;

const SERIES = '#2E5FA8';
const ALERT = '#d03b3b';
/** Something to go and switch on, which is neither a fault nor a rule. */
const WARN = '#b45309';
/** A neutral, deliberately NOT a third data colour: it marks the bars that are moving
 *  around the app rather than doing the work, so "blue = real work" stays readable. */
const NEUTRAL = '#9ca3af';

// ─── The API's shape ──────────────────────────────────────────────────────────
// Mirrors MobileUsageSnapshot. Blocks are optional here (and only here) because the
// response is untrusted input: a field this page reads must never be assumed present.

/** Ground staff (L1), their managers (L2-L5), and the people whose role never resolved.
 *  'unknown' is its own bucket on the API side and must never be folded into 'manager' —
 *  that fold is the double-counting bug the old "Who is using it" card shipped. */
type RoleGroup = 'ground' | 'manager' | 'unknown';

/** Card 1. One row per role group. */
interface PerDayUse {
  group: RoleGroup;
  /** Which days this row averaged: 'working' (ground staff — days they logged a shift) or
   *  'opened' (everyone else). The API decides it; the page only prints the matching
   *  words. Absent on an older build, and then the old wording is used. */
  basis?: string | null;
  people: number;
  medianActiveDays?: number | null;
  medianMinutes: number;
  /** The spread across people, printed as a range and never as a percentile. */
  p10Minutes?: number | null;
  p90Minutes?: number | null;
  medianPickups?: number | null;
}

/** Card 1's manager panel. `people` is printed on the card: with ~27 managers in a
 *  project, the reader has to see how few people the figure describes. */
interface ManagerSummary {
  people: number;
  approvalsMinutes: number;
  /** Singular "Screen" — the API's name. Zero or missing means "no comparison", not
   *  "they never looked at their team", so the sentence using it is guarded. */
  teamScreenMinutes?: number | null;
  approvalsSecondsPerVisit?: number | null;
  approvalsHours?: number | null;
  teamScreenHours?: number | null;
}

/** Card 2. One row per (day, group): a chart that does not filter by group plots two
 *  points per day. `settled` is false for the days still filling in. */
interface DayRow {
  day: string;
  settled?: boolean;
  group: RoleGroup;
  people: number;
  medianMinutes: number;
  p90Minutes?: number | null;
}

/** Card 3. Where a group's hours went, by business area. `navigation` is true for the
 *  home screen and the tab bar — getting somewhere, not doing anything. */
interface AreaRow {
  group: RoleGroup;
  area: string;
  navigation?: boolean;
  hours: number;
  sharePct: number;
  visits?: number | null;
  people?: number | null;
  secondsPerVisit?: number | null;
}

/**
 * Card 12. One row per APP PAGE — the full list card 3 summarises away. Rendered after card
 * 3 rather than after card 11, because that is where it reads; its NUMBER is the service's.
 *
 * `page` arrives in PLAIN ENGLISH from the API ("Task submission", "Random
 * attendance check"), mapped there from the route name, which is why this page has no label
 * map for it: a new screen appears here with no change, and a route the service has not
 * mapped falls back to its spaced-out name rather than vanishing. `area` is the same
 * business area card 3 groups by, so the two cards can be read against each other, and
 * `navigation` is true for Home and the tab roots — getting somewhere, not doing anything.
 */
interface PageTimeRow {
  area: string;
  page: string;
  navigation?: boolean;
  people: number;
  visits: number;
  hours: number;
  /** Typical (middle) seconds one visit lasted, and typical minutes one person spent on
   *  this page across the WHOLE window — "typical minutes a week", not per day. Both are
   *  medians over people on the API side, so a single heavy user cannot move either. */
  medianSecondsPerVisit?: number | null;
  medianMinutesPerPerson?: number | null;
  /** The route name the phone reported. Carried for support, NOT for the card: a program
   *  manager reads `page`, and nothing here prints this. */
  screen?: string | null;
}

/** Card 4. Opened the tab vs did the real thing inside it. Both labels come from the API
 *  so the wording stays specific per area ("opened a post" is not "opened a course"). */
interface ReachRow {
  area: string;
  openedLabel: string;
  openedPeople: number;
  openedMedianDays?: number | null;
  didLabel: string;
  didPeople: number;
  didMedianDays?: number | null;
}

/**
 * A person a blocker happened to, under an opened card 5 row.
 *
 * `daysHit` is that person's own days out of the row's `attemptDays`, and `gotItDone` is the
 * API's verdict that EVERY one of those days still ended with the job done — so a false is
 * the same person `stuckPeople` counts, and is the only cell on this page that means "ring
 * this one". It is a boolean and not a second count on purpose: the question being asked of
 * an opened row is whether to make a call, and a ratio invites arithmetic nobody wanted.
 */
interface BlockerPerson extends UsagePerson {
  daysHit?: number | null;
  events?: number | null;
  gotItDone?: boolean | null;
}

/** The people behind one blocker: the same wrapper as the follow-up lists (`UsagePersonList`
 *  on the API side), so `people` is a COUNT, `rows` is the array, and `hiddenLeavers` says
 *  how many were dropped. Capped by the API at the worst 200, which is why the panel also
 *  checks it against the row's own `people`. */
interface BlockerPeople {
  people: number;
  rows?: BlockerPerson[];
  truncated?: boolean;
  hiddenLeavers?: number | null;
}

/** Card 5. One plain-language thing that went wrong. */
interface BlockerRow {
  what: string;
  /** 'policy' is a rule saying no (a correct outcome), 'error' is something broken, and
   *  'setting' is a permission switched off on the phone — three different responses, so
   *  three groups on the card. */
  kind: 'policy' | 'error' | 'setting';
  events?: number | null;
  people: number;
  /** Person-days the failure happened on, and how many of those still ended with the job
   *  done. Recovery is the difference between wasted time and lost work. */
  attemptDays?: number | null;
  recoveredAttemptDays?: number | null;
  /** People with at least one day where they never finished the job: the list to call. */
  stuckPeople?: number | null;
  /** WHO it happened to, worst first. Absent, or carrying no rows, means the API named
   *  nobody for this row — it then does not open, and must not look as if it would. */
  who?: BlockerPeople | null;
}

/**
 * Card 9. One row per activity: the whole IST clock for one of the four things a day is
 * made of (mirrors `UsageDayShape`).
 *
 * `activity` arrives in PLAIN WORDS from the API ("Started the day"), which is why this
 * page has no label map for it — the service owns those words and a new activity appears
 * here with no change. `perHour` is 24 counts, 00:00-00:59 first, of times the job was
 * LOGGED; it is never a count of people and never time spent in the app. The busiest hour
 * and its share are worked out by the API from those same counts, so the label and the
 * figure can never describe different hours — this page reformats the band into plain
 * clock words and does not recompute it. Reformatting is where that guarantee can still be
 * lost: the API's band is INCLUSIVE of its last hour, and a reader of plainBand() who
 * takes the end at face value prints an hour less than the percentage beside it covers.
 */
interface DayShapeRow {
  activity: string;
  /** The API's stable name for the same activity ('attendance_check_in'); absent on an
   *  older build, and then this panel's figures simply do not open. */
  action?: string | null;
  perHour?: number[] | null;
  records: number;
  people?: number | null;
  busiestHour?: number | null;
  /** The band the share covers, as the API measured it: INCLUSIVE at both ends, i.e.
   *  '10:00-12:59' for the hours 10, 11 and 12 (bandLabel() in posthog-usage.service.ts
   *  hardcodes the ':59'). Three hours, not two — see plainBand(). */
  busiestHours?: string | null;
  busiestHoursPct?: number | null;
}

/**
 * Card 9's honesty figures: what the evening peak in task reports actually is (mirrors
 * `UsageTaskTiming`).
 *
 * Both numbers exist to stop the one wrong reading of that peak — "they write the day up
 * at home from memory". They are printed on the card, and the API sends null when there
 * were no task reports to time, in which case the card keeps the plain sentence and drops
 * the figures rather than printing a percentage of nothing.
 */
interface TaskTiming {
  /** Task reports in the window: the denominator. */
  submissions?: number | null;
  /** ... and how many arrived after that same person's own check-out that day. */
  afterOwnCheckOut?: number | null;
  afterOwnCheckOutPct?: number | null;
  /** Person-days with more than one task report ... */
  personDaysWithSeveral?: number | null;
  /** ... and how many of those had every report inside `togetherWithinMinutes`. */
  personDaysLoggedTogether?: number | null;
  loggedTogetherPct?: number | null;
  togetherWithinMinutes?: number | null;
  medianSpreadMinutes?: number | null;
}

/**
 * Card 10. One row per KIND of course, never per course (mirrors `UsageTraining`): the
 * start and the finish carry no course id, so these are totals over the same window and
 * the same people and must not be read as one course's drop-off.
 *
 * THE COLUMNS DO NOT MEAN THE SAME THING ACROSS THE TWO ROWS, and the card says so: per
 * the API, the course player has no failure path at all and the in-app lessons have no
 * give-up path, so "353 given up, 0 failed" beside "0 given up, 303 failed" is the
 * difference between two pieces of code and not between two sets of learners. Which is
 * why the broken-player call is `finishMayNotBeReported` — the API's own flag — and never
 * a comparison this page invents from the numbers.
 */
interface TrainingRow {
  /** Already the words to print ('Courses that open in the course player'). */
  kind: string;
  /** People who started at least one, and how many starts that was between them. */
  people: number;
  started: number;
  /** Starts the app could not get open at all. */
  couldNotOpen?: number | null;
  finished: number;
  finishedPeople?: number | null;
  gaveUp: number;
  gaveUpPeople?: number | null;
  /** How long the content was open before they left it — reading and idle time included,
   *  so it is not a measure of effort and the card must not present it as one. */
  gaveUpMedianSeconds?: number | null;
  /** Finishes the app sent that came back refused — NOT a crash and not a verdict on the
   *  content. Per the API, for the in-app lessons this is the server declining the
   *  submission (the module was not passed, or it had already been sent), and the course
   *  player cannot report a failure at all, so its figure is always zero. That is why the
   *  card calls the column "not passed" and never "would not complete". */
  finishFailed: number;
  finishFailedPeople?: number | null;
  /** The API's own verdict: nothing failed and more were left than finished, which is the
   *  fingerprint of a player that never reports a finish. Read, never recomputed here. */
  finishMayNotBeReported?: boolean;
  /** The API's stable key for this kind ('scorm' | 'other'); absent on an older build,
   *  and then its figures simply do not open. */
  kindKey?: string | null;
}

/**
 * Cards 7, 8 and 11 — all three return the same columns, and nothing is read here for one
 * card only. Card 11's notification OPENS in particular are a filter inside its query and
 * never a column on the row: a field for them would read a confident `0` out of a payload
 * that never carried one.
 *
 * The API also puts `answeredChecks` on a card 11 row, and this page deliberately does not
 * read it. The card prints that figure against the list's own size ("279 of these 294"), and
 * the CONTROLLER already recounts `noNotificationsStillAnsweredChecks` over the rows that
 * survive the leaver filter — so the two halves of that sentence come from the same place.
 * Summing the column here instead would be a second, parallel answer to one question.
 */
/**
 * Card 7's last column: how this person's day is actually being marked.
 *
 * An unknown value is printed as "not known" rather than guessed at. The page is served to
 * a browser that may have been left open across a deploy, so a row from an older API build
 * arrives with no `dayMarking` at all — and "not known" is then the only true answer.
 */
const dayMarkingLabel = (marking: unknown): string => {
  if (marking === 'market_request') return 'Market attendance request';
  if (marking === 'not_from_the_app') return 'Nothing in the app';
  return 'Not known';
};

interface WorklistRow extends UsagePerson {
  activeDays?: number | null;
  minutes?: number | null;
  checkIns?: number | null;
  /** Card 7 only: 'market_request' when the person raises a market-attendance request
   *  instead of checking in, 'not_from_the_app' when the app has no record either way.
   *  Absent on the other two lists, and on an older API build — the column then reads
   *  "not known", which is the truth rather than a guess. */
  dayMarking?: string | null;
}

interface Worklist {
  /** A COUNT of people, not the rows — and a count of the people SHOWN, so the headline
   *  sentence can never promise more names than the table holds. The people the API hid
   *  are counted separately, in `hiddenLeavers`. */
  people: number;
  rows?: WorklistRow[];
  truncated?: boolean;
  /** People left out because 1Office says they have exited. Printed on the card so a list
   *  that shrank is never a mystery; a person whose status is UNKNOWN is not in here and is
   *  still listed. */
  hiddenLeavers?: number | null;
}

interface Snapshot {
  generatedAt: string;
  projectId: number;
  days: number;
  disabled: boolean;
  /** The whole settled IST days every aggregate card covers, inclusive. */
  window?: { from: string; to: string } | null;
  /** First IST day ('YYYY-MM-DD') still filling in; every earlier day is settled. */
  provisionalFrom?: string;
  degraded: string[];
  perDayUse?: PerDayUse[];
  managers?: ManagerSummary | null;
  dayByDay?: DayRow[];
  timeShare?: AreaRow[];
  /** Share (0-100) of card 3's hours that the phone timed rather than the API working out
   *  from event gaps. Card 3's label is read from this and is never suppressed. */
  timeShareMeasuredPct?: number | null;
  /** Card 4, the full page-by-page list behind card 3. Ground staff only. */
  pageTime?: PageTimeRow[];
  reach?: ReachRow[];
  groundPeople?: number;
  blockers?: BlockerRow[];
  noAttendance?: Worklist | null;
  /** Active on at least this many days is what card 7 means by "most days". */
  noAttendanceMinActiveDays?: number | null;
  barelyUsing?: Worklist | null;
  /** Under this many minutes for the whole window is what card 8 means by "barely". */
  barelyUsingThresholdMinutes?: number | null;
  /** Card 9. Built from logged work only — there is no hour in the app's own time
   *  reporting, so a card drawn from this must not describe time on screen. */
  dayShape?: DayShapeRow[];
  taskTiming?: TaskTiming | null;
  /** Card 10. */
  training?: TrainingRow[];
  /** Card 11, the fourth named follow-up list. */
  noNotifications?: Worklist | null;
  /** Active on at least this many days is what card 11 means by "uses the app". */
  noNotificationsMinActiveDays?: number | null;
  /** What a typical person in this project opened, for the comparison on card 11. The
   *  listed people are all at zero, so this is the only thing that says whether zero is
   *  unusual here. */
  notificationMedianOpens?: number | null;
  /** How many of the listed people still answered a surprise check. Printed on the card:
   *  it is the measured reason a zero here cannot be read as "nothing was delivered". */
  noNotificationsStillAnsweredChecks?: number | null;
  platforms?: Array<{ platform: string; people: number }>;
}

const DAY_OPTIONS = [7, 14, 30, 90];

/** What each role group is called on screen. */
const GROUP_LABEL: Record<RoleGroup, string> = {
  ground: 'ground team',
  manager: 'managers',
  unknown: 'people whose role could not be identified',
};

/** `degraded` carries the API's own block keys — the keys of BLOCKS in
 *  posthog-usage.service.ts, and nothing else. They are code names, so they are
 *  translated to the card titles the reader can see; an unmapped key falls back to a
 *  generic sentence rather than printing camelCase at a non-technical audience.
 *
 *  ONE BLOCK PER ENTRY. Card 9 is drawn from two fields (`dayShape` and `taskTiming`) but
 *  from ONE block, `dayShape`, which the service says explicitly: both halves "live or
 *  degrade together". So there is no `taskTiming` key here — an entry for a key the API
 *  cannot send is a line nobody can ever see, and it was hiding the fact that the only way
 *  to test a degraded card 9 is to degrade `dayShape`. */
const CARD_TITLES: Record<string, string> = {
  perDayUse: 'Minutes in the app per working day',
  managers: 'What managers spend that time on',
  dayByDay: 'Time in the app, day by day',
  timeShare: 'Where the hours go',
  pageTime: 'How long people spend on each page',
  reach: 'Opened it vs actually used it',
  blockers: 'What stopped people',
  noAttendance: 'Marking attendance some other way',
  barelyUsing: 'Barely using the app',
  dayShape: 'When the day actually happens',
  training: 'Training: started, finished, given up',
  noNotifications: "Never sees the app's notifications",
  platforms: 'Which phones the team is on',
};

/**
 * Did the API fail to read this block?
 *
 * THE DIFFERENCE THIS DRAWS IS THE WHOLE POINT OF THE CARD-BY-CARD GUARDS. A block that
 * threw does NOT arrive as null: the service's empty() factory leaves `dayShape` and
 * `training` as `[]` and every worklist as `{ people: 0, rows: [], truncated: false }`,
 * and says so only by pushing the block's key into `degraded`. So an emptiness check on
 * its own cannot tell "nobody did this" from "we could not look", and the page used to
 * print the banner "… could not be loaded this time" above a card asserting nothing had
 * happened — the two halves of one screen contradicting each other, with the reassuring
 * half in bigger type.
 *
 * `degraded` is untrusted input like everything else in the payload, so a non-array is
 * treated as "nothing is degraded" rather than thrown over.
 */
const unreadable = (data: Snapshot | null | undefined, block: string): boolean => {
  const keys = data?.degraded;
  return Array.isArray(keys) && keys.includes(block);
};

/**
 * A share, or null when there is nothing to divide by.
 *
 * Null rather than an em dash, so a caller drops the whole phrase instead of printing
 * "out of 0 days worked (—)". A real but tiny share floors at "under 1%": rounding it to
 * "0%" next to a non-zero count reads as a contradiction to a non-technical reader.
 */
const share = (part: unknown, whole: unknown): string | null => {
  const w = n(whole);
  if (w <= 0) return null;
  const pct = (n(part) / w) * 100;
  if (pct > 0 && pct < 1) return 'under 1%';
  if (pct < 100 && pct > 99) return 'over 99%';
  return `${Math.round(pct)}%`;
};

/**
 * A count of jobs done, with the caller's own unit ("5,071 times", "7 notifications").
 *
 * Takes the {one, many} pair its siblings people()/days()/seconds() take, because the
 * counts it prints DO reach one: a store with a single record of an activity rendered
 * "Logged 1 times", and a project where a typical person opens one notification is exactly
 * the kind of project card 11 exists for.
 */
const times = (v: unknown, unit: { one: string; many: string }): string => {
  const x = Math.round(n(v));
  return `${x.toLocaleString()} ${x === 1 ? unit.one : unit.many}`;
};

/** The two units times() counts in on this page: records of a job being logged (card 9)
 *  and notifications opened (card 11). */
const TIMES = { one: 'time', many: 'times' } as const;
const NOTIFICATIONS = { one: 'notification', many: 'notifications' } as const;

/** A short duration in the unit a reader thinks in: seconds up to a minute and a half,
 *  minutes after that. Used for "how long before they gave up". */
/**
 * A span of hours with its unit. Under ten the decimal is the story (0.4 hours is not
 * "0 hours"); above it, it is noise on a table of totals.
 *
 * A real but tiny span floors at "under 0.1 hours", the same house rule share() keeps for a
 * tiny percentage: a "0 hours" printed against thousands of visits reads as a contradiction,
 * and the caller has no way to tell it apart from a page nothing was measured on.
 */
const hours = (v: unknown): string => {
  const x = n(v);
  if (x > 0 && x < 0.05) return 'under 0.1 hours';
  const r = x < 10 ? Math.round(x * 10) / 10 : Math.round(x);
  return `${r.toLocaleString()} ${r === 1 ? 'hour' : 'hours'}`;
};

/** A plain count, grouped for the eye. Table cells whose unit is in the column heading. */
/**
 * An hour of the IST clock in plain words: 9 → '9 AM', 21 → '9 PM', noon and midnight by
 * name. 24 is accepted because the END of an hour range is one past its last hour, so a
 * band ending at 23:59 is printed as "to midnight".
 */
const clock = (hour: number): string => {
  const h = ((Math.round(hour) % 24) + 24) % 24;
  if (h === 0) return 'midnight';
  if (h === 12) return 'noon';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
};

/**
 * A share the API has already worked out, printed with its unit.
 *
 * Same flooring rule as share(): a real but tiny share reads "under 1%", because a "0%"
 * printed beside a non-zero count reads as a contradiction to a non-technical reader.
 */
const percent = (v: unknown): string => {
  const x = n(v);
  if (x > 0 && x < 1) return 'under 1%';
  if (x < 100 && x > 99) return 'over 99%';
  return `${Math.round(x)}%`;
};

/**
 * The API's measured hour band ('10:00-12:59') in the plain clock this page speaks
 * ('10 AM and 1 PM'), for a sentence of the form "x% of them between …".
 *
 * Reformatted rather than recomputed: the band and the share it carries were worked out
 * together from the same counts, so a band this page derived for itself could disagree
 * with the percentage printed next to it. Null when the string is not a band, in which
 * case the card leaves the sentence out rather than printing a 24-hour clock at a reader
 * this whole page writes plainly for.
 *
 * THE END OF THE API'S BAND IS INCLUSIVE, and getting that wrong is not a wording slip —
 * it silently narrows the sentence to less than the percentage beside it. `bandLabel()` in
 * posthog-usage.service.ts prints '10:00-12:59' for the hours 10, 11 and 12, and the share
 * is summed over all three; on project 170's check-ins that last hour is 4,742 of the
 * 12,763 the 89% counts. So a band ending at :59 ends at the TOP of the next hour, and
 * "between 10 AM and 1 PM" is the only phrasing that covers what was measured. An end at
 * :00 is read as already exclusive, which is what the string would mean if the service
 * ever switched conventions: either way the words cover the hours the number does.
 */
const plainBand = (band: string | null | undefined): string | null => {
  const m = /^(\d{1,2}):(\d{2})\s*[-–to\s]+\s*(\d{1,2}):(\d{2})$/.exec(String(band ?? '').trim());
  if (!m) return null;
  const endHour = Number(m[3]);
  // Only the inclusive form is nudged on: clock() takes 24 and prints "midnight", so a
  // band ending at 23:59 reads "to midnight" rather than wrapping to the previous day.
  const endsAt = Number(m[4]) > 0 ? endHour + 1 : endHour;
  return `${clock(Number(m[1]))} and ${clock(endsAt)}`;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-10-03' → '3 Oct'. Parsed by hand: `new Date('2026-10-03')` is UTC midnight and
 *  would print the previous day for a reader west of Greenwich. */
const dayLabel = (day: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day ?? ''));
  if (!m) return String(day ?? '');
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${Number(m[3])} ${month}` : String(day);
};

/** The dates the figures actually cover, for the one line at the top of the page. */
const windowRange = (w: Snapshot['window']): string | null =>
  w?.from && w?.to ? `${dayLabel(w.from)} to ${dayLabel(w.to)}` : null;

/**
 * How a card refers to its window. Says which days, never "this week": the newest day in
 * the figures is two days old, so "this week" would be a false claim of recency.
 */
const windowPhrase = (daysCovered: number, w: Snapshot['window']): string => {
  const count = Math.max(1, Math.round(n(daysCovered)));
  const unit = count === 1 ? 'day' : 'days';
  return w?.to
    ? `in the ${count} complete ${unit} to ${dayLabel(w.to)}`
    : `in the last ${count} complete ${unit}`;
};

/**
 * Card 3's honesty label, read from timeShareMeasuredPct and always printed.
 *
 * The API documents 0 as "nothing was measured" (including "no data") and 100 as "nothing
 * was estimated", so neither end can overstate. Everything in between says both halves out
 * loud: a program manager deciding where the team's hours go is entitled to know how much
 * of the split was timed and how much inferred.
 */
const measuredNote = (measuredPct: unknown): string => {
  const pct = Math.round(n(measuredPct));
  if (pct >= 100) {
    return 'All of this time was timed by the phone while the app was open on screen.';
  }
  if (pct <= 0) {
    return 'None of this time was timed by the phone — all of it is worked out from when the app next reported, so treat the split as a rough guide.';
  }
  return `About ${pct}% of this time was timed by the phone while the app was open on screen; the rest is worked out from when the app next reported, so treat the smaller slices as approximate.`;
};

/**
 * Card 9's subtitle, which has two jobs: say what these hours ARE, and shut down the one
 * wrong reading of the evening peak in task reports.
 *
 * The figures come from the API; when they are missing the sentence keeps the plain
 * statement and drops the numbers, because a number invented here would be exactly the
 * kind of confident wrong answer this page was rewritten to stop.
 *
 * Measured directly against the events for project 170 over 27 Sep - 3 Oct 2026, using the
 * API's own definitions: 2 of 32,319 task reports arrived after that person's LAST
 * check-out of the same day, and 10,718 of the 12,364 days where somebody logged more than
 * one task had all of them inside 15 minutes (a typical such day spans 0.7 min). Both say
 * the same thing — the work is written up as the shift ends, on the shop floor.
 */
const hourCardAction = (t: TaskTiming | null | undefined): string => {
  const head = 'These hours are when work was LOGGED in the app, not when the app was open on screen — '
    + 'the app reports its time in windows over an hour long, so it cannot say what anyone was doing at a given hour.';
  const tail = 'If a peak sits at the wrong time for a store, check that store\'s shift timings before asking anyone about it.';

  const submissions = n(t?.submissions);
  const after = n(t?.afterOwnCheckOut);
  const multiDays = n(t?.personDaysWithSeveral);
  const within = Math.round(n(t?.togetherWithinMinutes));

  // The shares are taken with this page's own share(), not the API's rounded percentages,
  // for one reason: the measured figure is a couple of reports in thirty thousand, which
  // rounds to 0.0 on the wire. "0% (2 of 32,319)" reads as a contradiction, and the house
  // rule for a real-but-tiny share is to floor it at "under 1%". Numerator and denominator
  // come from the same block and the same window, so there is nothing to disagree with.
  const facts: string[] = [];
  const afterShare = submissions > 0 ? share(after, submissions) : null;
  const togetherShare = multiDays > 0 ? share(t?.personDaysLoggedTogether, multiDays) : null;
  if (afterShare) {
    facts.push(`just ${afterShare} of task reports `
      + `(${Math.round(after).toLocaleString()} of ${Math.round(submissions).toLocaleString()}) `
      + "were sent after that same person's own check-out");
  }
  if (togetherShare && within > 0) {
    facts.push(`on ${togetherShare} of the days somebody logged more than one task, `
      + `every one of them went in within ${within} minutes of the others`);
  }

  const evening = facts.length > 0
    ? `The late peak is the shift ending, not a write-up afterwards: ${facts.join(', and ')}.`
    : 'The late peak is the end of the shift: tasks are logged as the day closes, not written up later from memory.';

  return `${head} ${evening} ${tail}`;
};

/** The degraded notice, in card names. */
const degradedSentence = (keys: string[]): string => {
  const mapped = keys.map((k) => CARD_TITLES[k]);
  // De-duplicated because two block keys MAY share one card title: nothing stops the
  // service splitting a card's query in two, and a card named twice in one sentence reads
  // as two separate failures. (No pair shares a title today — card 9's two halves come
  // from the single `dayShape` block.)
  const named = [...new Set(mapped.filter((t): t is string => Boolean(t)))];
  if (named.length === 0 || mapped.some((t) => !t)) {
    return 'Some cards could not be loaded this time. Everything else below is current.';
  }
  const list = named.length === 1
    ? named[0]
    : `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`;
  return `${list} could not be loaded this time. Everything else below is current.`;
};

/**
 * The rows for the group a card is about, which is the ground team.
 *
 * A project whose roles did not resolve would otherwise render an empty card, so this
 * falls back to the largest group present AND reports which one it picked, so the card can
 * say whose figures it is showing rather than implying they are the ground team's.
 */
const groundOr = <T extends { group: RoleGroup }>(rows: T[] | undefined): { group: RoleGroup; rows: T[] } => {
  const all = rows ?? [];
  const ground = all.filter((r) => r.group === 'ground');
  if (ground.length > 0) return { group: 'ground', rows: ground };
  const counts = new Map<RoleGroup, number>();
  for (const r of all) counts.set(r.group, (counts.get(r.group) ?? 0) + 1);
  let best: RoleGroup = 'ground';
  let most = 0;
  for (const [group, count] of counts) if (count > most) { best = group; most = count; }
  return { group: best, rows: all.filter((r) => r.group === best) };
};

interface CardProps {
  title: string;
  /** One line saying what to do when the card looks wrong. Required — a card nobody can
   *  act on is one of the cards this page was rewritten to remove. A node rather than a
   *  string because card 9's figures live in this line and have to be openable. */
  action: ReactNode;
  children: React.ReactNode;
}

const Card = ({ title, action, children }: CardProps) => (
  <section className="rounded-lg border border-gray-100 bg-white p-6">
    <div className="mb-4">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <p className="mt-1 text-sm text-gray-500">{action}</p>
    </div>
    {children}
  </section>
);

const Unavailable = ({ what }: { what: string }) => (
  <p className="rounded border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
    {what}
  </p>
);

/**
 * "3 people who have left are not shown."
 *
 * WHY THIS SENTENCE IS NOT OPTIONAL. The API hides the people 1Office says have exited from
 * every follow-up list, because the barely-using list was being topped by them and a leaver
 * is nobody to ring. But a list that silently got shorter is a list a program manager stops
 * trusting — "I know there were more than this last week" — so the number that was hidden is
 * printed wherever the hiding happened. Nothing is printed when nothing was hidden: a
 * standing "0 people who have left" would be noise on every card, every week.
 *
 * It says "who have left", never "inactive" or "exited": a person whose status the directory
 * never answered for is NOT in this count and is still on the list (see the header note).
 */
const HiddenLeavers = ({ count: hidden, className = 'mb-3' }: { count: unknown; className?: string }) => {
  const x = Math.round(n(hidden));
  if (x <= 0) return null;
  return (
    <p className={`${className} text-xs text-gray-500`}>
      {x === 1
        ? '1 person who has left is not shown'
        : `${x.toLocaleString()} people who have left are not shown`}
      , so this is only people you can still follow up with.
    </p>
  );
};

/** One of the two groups in the headline card. A spread is low..high, shown as a range. */
/**
 * A number you can open.
 *
 * Every figure on this page is a count or an average over PEOPLE, and the question a
 * program manager asks next is always "which people?". So the figures that can answer that
 * are controls, not text.
 *
 * A BUTTON, never a styled <span> with an onClick: these are the only interactive things in
 * the middle of this page, and a reader on a keyboard has to be able to reach them and know
 * they arrived. The dotted underline is what says "there is more behind this" without
 * turning a page of statistics into a page of links.
 */
const DrillNumber = ({ onOpen, label, children }: { onOpen: () => void; label: string; children: ReactNode }) => (
  <button
    type="button"
    onClick={onOpen}
    aria-label={label}
    title={label}
    className="rounded underline decoration-dotted underline-offset-2 transition-colors hover:text-blue-700 hover:decoration-solid focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
  >
    {children}
  </button>
);

const GroupPanel = ({ label, group, note, onOpenPeople }: { label: string; group: PerDayUse | null | undefined; note: string; onOpenPeople?: () => void }) => {
  if (!group || n(group.people) <= 0) {
    return (
      <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
        <div className="mt-2 text-sm text-gray-500">Nobody in this group reported a full day yet.</div>
      </div>
    );
  }
  const low = n(group.p10Minutes);
  const high = n(group.p90Minutes);
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-2 text-3xl font-bold tabular-nums text-gray-900">{minutes(group.medianMinutes)}</div>
      <div className="mt-1 text-sm text-gray-600">
        {group.basis === 'working' ? 'on a working day, across' : 'on a day they used it, across'}{' '}
        {onOpenPeople
          ? (
            <DrillNumber onOpen={onOpenPeople} label={`See the ${people(group.people)} these figures are worked out over`}>
              {people(group.people)}
            </DrillNumber>
          )
          : people(group.people)}
      </div>
      {high > 0 && (
        <div className="mt-2 text-sm text-gray-600">
          Most are between {minutes(low)} and {minutes(high)}.
        </div>
      )}
      {n(group.medianPickups) > 0 && (
        <div className="mt-1 text-sm text-gray-600">
          They pick the phone up {Math.round(n(group.medianPickups) * 10) / 10} times a day.
        </div>
      )}
      {n(group.medianActiveDays) > 0 && (
        <div className="mt-1 text-sm text-gray-600">
          {group.basis === 'working'
            ? <>A typical person worked on {days(group.medianActiveDays)} of these.</>
            : <>A typical person opened it on {days(group.medianActiveDays)}.</>}
        </div>
      )}
      <div className="mt-3 text-xs text-gray-500">{note}</div>
    </div>
  );
};

export function MobileUsageReportPage() {
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);

  const [dayCount, setDayCount] = useState(14);
  // Which number is open, or null. One piece of state for the whole page: only one window
  // can be open at a time, and the window itself holds no memory of the last one — a second
  // click must never show the first click's list while the new one loads.
  const [openDrill, setOpenDrill] = useState<DrillRequest | null>(null);
  const [data, setData] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [forbidden, setForbidden] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (opts: { refresh?: boolean } = {}) => {
    if (!selectedProjectId) {
      setLoading(false);
      return;
    }
    opts.refresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const res = await mobileUsageAPI.getSnapshot({
        projectId: selectedProjectId,
        days: dayCount,
        refresh: opts.refresh,
      });
      setData(res.data?.data ?? null);
      setForbidden(null);
    } catch (err: any) {
      // A 403 is the access answer, not a failure — render the access panel instead.
      if (err?.response?.status === 403) {
        setForbidden(err.response?.data?.message ?? 'You do not have access to Mobile Usage.');
      } else {
        setError(err?.response?.data?.message ?? 'Could not load usage analytics.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedProjectId, dayCount]);

  useEffect(() => { void load(); }, [load]);

  // The window the FIGURES cover, which is the one the API answered with — not the one
  // currently selected in the dropdown, which may not have loaded yet.
  const covered = n(data?.days) > 0 ? n(data?.days) : dayCount;
  const win = data?.window ?? null;
  const phrase = windowPhrase(covered, win);

  // Card 2: one group only, or the chart draws two points per day.
  const dayChart = useMemo(() => {
    const picked = groundOr(data?.dayByDay);
    return {
      group: picked.group,
      rows: picked.rows
        .slice()
        .sort((a, b) => String(a.day).localeCompare(String(b.day)))
        .map((r) => ({
          day: r.day,
          dayLabel: dayLabel(r.day),
          people: n(r.people),
          medianMinutes: n(r.medianMinutes),
          // `settled` is the API's own flag; only an explicit false is "still filling in".
          unsettled: r.settled === false,
        })),
    };
  }, [data]);

  const hasUnsettledDays = dayChart.rows.some((r) => r.unsettled);

  // Card 3: time share for the same group, biggest first, navigation areas marked so the
  // chart can grey them.
  const areaChart = useMemo(() => {
    const picked = groundOr(data?.timeShare);
    return {
      group: picked.group,
      rows: picked.rows
        .filter((a) => n(a.hours) > 0)
        .slice()
        .sort((a, b) => n(b.sharePct) - n(a.sharePct))
        .map((a) => ({
          area: a.area,
          sharePct: Math.round(n(a.sharePct) * 10) / 10,
          hours: Math.round(n(a.hours)),
          secondsPerVisit: n(a.secondsPerVisit),
          visits: n(a.visits),
          navigation: Boolean(a.navigation),
        })),
    };
  }, [data]);

  const navigationShare = useMemo(
    () => areaChart.rows.filter((a) => a.navigation).reduce((sum, a) => sum + a.sharePct, 0),
    [areaChart],
  );

  /**
   * Card 12: every page, inside its business area, areas and pages both ordered by total
   * hours so the table reads top-down as "where the time actually went".
   *
   * The API's row order is deliberately NOT relied on — it groups by area for card 3's
   * business order, and this card is sorted by a different thing. Hours are summed per area
   * here rather than read from card 3's `hours`: card 3 is a different query over a
   * different grouping, and two numbers that nearly agree are worse than one.
   *
   * A page with no time AND no visits is dropped: it is a row of zeros, which is not a
   * finding, and on a 57-page table it is the difference between a readable card and a
   * wall. A page with visits but no measured time stays — that is an older phone's data,
   * not an unused page.
   */
  const pageAreas = useMemo(() => {
    const rows = (data?.pageTime ?? []).filter((r) => n(r.hours) > 0 || n(r.visits) > 0);
    const byArea = new Map<string, PageTimeRow[]>();
    for (const row of rows) {
      const area = String(row.area ?? '').trim() || 'Something else';
      const list = byArea.get(area);
      if (list) list.push(row); else byArea.set(area, [row]);
    }
    return [...byArea.entries()]
      .map(([area, pages]) => ({
        area,
        hours: pages.reduce((sum, p) => sum + n(p.hours), 0),
        // Grey the heading only when the WHOLE area is navigation (Home, the tab roots).
        // A work area holding one navigation page is still a work area.
        navigation: pages.every((p) => Boolean(p.navigation)),
        pages: pages.slice().sort((a, b) => n(b.hours) - n(a.hours)),
      }))
      .sort((a, b) => b.hours - a.hours);
  }, [data]);

  /** The longest page on the card, for the ranking bars. Taken across every area so the
   *  bars compare pages with each other and not each page with its own area's biggest. */
  const pageTimeMax = useMemo(
    () => Math.max(...pageAreas.flatMap((g) => g.pages.map((p) => n(p.hours))), 1),
    [pageAreas],
  );

  // Card 4, biggest drop-off first: the areas people open but do not use are the ones to fix.
  const reachRows = useMemo(
    () =>
      (data?.reach ?? [])
        .filter((d) => n(d.openedPeople) > 0)
        .slice()
        .sort((a, b) => n(a.didPeople) / n(a.openedPeople) - n(b.didPeople) / n(b.openedPeople)),
    [data],
  );

  // Card 5. Three kinds, three different responses, so three groups — a permission the
  // person switched off is neither broken software nor a rule saying no, and lumping it in
  // with the errors prescribes the wrong fix.
  const byKind = useCallback(
    (kind: BlockerRow['kind']) =>
      (data?.blockers ?? []).filter((b) => b.kind === kind).slice().sort((a, b) => n(b.people) - n(a.people)),
    [data],
  );
  const errorBlockers = useMemo(() => byKind('error'), [byKind]);
  const settingBlockers = useMemo(() => byKind('setting'), [byKind]);
  const policyBlockers = useMemo(() => byKind('policy'), [byKind]);
  const anyBlockers = errorBlockers.length + settingBlockers.length + policyBlockers.length > 0;

  /**
   * Card 9. One small chart per activity, all of them on the SAME hour axis so the eye can
   * compare them — a check-in peak at 11 AM beside a task-report peak at 9 PM is the whole
   * finding, and it is only visible when the axes match.
   *
   * The API sends the whole 24-hour clock per activity, which is mostly empty for a day
   * shift, so the axis is cut to the hours anything at all was logged in — once, across
   * every activity, never per chart. An hour inside that span with nothing in it keeps its
   * zero: dropping it would close the gap and move the peak.
   *
   * The API's own row order is kept (it is the order a day runs), and so are its busiest
   * hour and share: this page reads them, it does not work them out again.
   */
  const hourPanels = useMemo(() => {
    const rows = (data?.dayShape ?? []).filter((r) => Array.isArray(r?.perHour) && r.perHour.length > 0);
    if (rows.length === 0) return [];

    const hoursWithWork = rows.flatMap((r) =>
      (r.perHour ?? []).flatMap((count, hour) => (n(count) > 0 ? [hour] : [])));
    const from = hoursWithWork.length > 0 ? Math.max(0, Math.min(...hoursWithWork)) : 0;
    const to = hoursWithWork.length > 0 ? Math.min(23, Math.max(...hoursWithWork)) : 23;

    return rows
      // An activity nothing was logged for is left out rather than drawn as a flat line,
      // which a reader takes for "this never happens here" rather than "no data".
      .filter((r) => n(r.records) > 0)
      .map((r, i) => ({
        key: `${r.activity}-${i}`,
        activity: String(r.activity ?? ''),
        action: typeof r.action === 'string' ? r.action : '',
        records: n(r.records),
        people: n(r.people),
        busiestHour: Math.round(n(r.busiestHour)),
        band: plainBand(r.busiestHours),
        bandPct: n(r.busiestHoursPct),
        series: Array.from({ length: Math.max(0, to - from + 1) }, (_, k) => ({
          hour: from + k,
          records: Math.round(n((r.perHour ?? [])[from + k])),
        })),
      }));
  }, [data]);

  // Card 10. The API returns a row per kind whether or not anything happened in it, so a
  // kind nobody touched is dropped here — a column of zeros is not a finding, and the two
  // halves of this card are read side by side.
  const trainingRows = useMemo(
    () => (data?.training ?? []).filter((t) => n(t.started) > 0 || n(t.people) > 0),
    [data],
  );

  if (forbidden) {
    return (
      <AccessDeniedPanel
        message={forbidden}
        onLogout={() => { clearAuth(); navigate('/login'); }}
      />
    );
  }

  if (!selectedProjectId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Mobile Usage</h1>
        <section className="rounded-lg border border-gray-100 bg-white p-6 text-sm text-gray-600">
          Select a project first — this report always shows one project at a time.
        </section>
      </div>
    );
  }

  const perDay = data?.perDayUse ?? [];
  const groundUse = perDay.find((g) => g.group === 'ground') ?? null;
  const managerUse = perDay.find((g) => g.group === 'manager') ?? null;
  const unknownUse = perDay.find((g) => g.group === 'unknown') ?? null;

  // THE THREE NAMED LISTS ARE NULLED WHEN THEIR BLOCK FAILED, not just when the field is
  // missing. A failed block arrives as a zeroed object — `{ people: 0, rows: [] }` from
  // empty() in posthog-usage.service.ts — so "is it there" is not the same question as
  // "could it be read", and a list that only checked the first printed "everyone who used
  // the app also marked attendance" over a query that never ran. Resolving it here keeps
  // every guard below as a plain `!list`, instead of three call sites each remembering to
  // ask twice.
  const noAttendance = unreadable(data, 'noAttendance') ? null : (data?.noAttendance ?? null);
  const barely = unreadable(data, 'barelyUsing') ? null : (data?.barelyUsing ?? null);

  // Card 7's two groups, counted from the rows ON SCREEN rather than returned as totals by
  // the API: the controller hides people who have left AFTER the query ran, so a total from
  // the query could promise 16 market-request people above a table holding 14.
  // Array.isArray, not `?? []`: `rows` comes straight off an HTTP response, and counting
  // over a non-array would throw inside the render and take the whole report down with it
  // (WorklistTable guards its own copy for the same reason).
  const noAttendanceRows = Array.isArray(noAttendance?.rows) ? noAttendance.rows : [];
  const marketRouteCount = noAttendanceRows.filter((r) => r.dayMarking === 'market_request').length;
  const otherRouteCount = noAttendanceRows.filter((r) => r.dayMarking === 'not_from_the_app').length;

  const minActiveDays = Math.round(n(data?.noAttendanceMinActiveDays));
  const barelyThreshold = Math.round(n(data?.barelyUsingThresholdMinutes));
  const range = windowRange(win);

  // Card 11: the third named list, with its own threshold exposed the same way cards 7
  // and 8 expose theirs, so the card can say what it means rather than "most days".
  // Cards 9 and 10 ask the same question as the lists, but of a block that owns a whole
  // card rather than a table: did it fail, or did it arrive with nothing in it? A field
  // the payload left out entirely counts as a failure too — "nothing was logged" is a
  // measurement, and this page does not make one up from an absent field.
  const dayShapeUnreadable = unreadable(data, 'dayShape') || data?.dayShape === undefined;
  const trainingUnreadable = unreadable(data, 'training') || data?.training === undefined;

  const noNotifications = unreadable(data, 'noNotifications') ? null : (data?.noNotifications ?? null);
  const notificationsMinDays = Math.round(n(data?.noNotificationsMinActiveDays));
  const medianOpens = Math.round(n(data?.notificationMedianOpens));
  const stillAnsweredChecks = Math.round(n(data?.noNotificationsStillAnsweredChecks));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <Smartphone className="h-6 w-6 text-primary-600" />
            Mobile Usage
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            How your ground team is working in the app, and who to follow up with.
            {data?.generatedAt && (
              <> Updated {new Date(data.generatedAt).toLocaleTimeString()}.</>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <select
            id="mobile-usage-days"
            value={dayCount}
            onChange={(e) => setDayCount(Number(e.target.value))}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:outline-none"
          >
            {DAY_OPTIONS.map((d) => (
              <option key={d} value={d}>{d} complete days</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void load({ refresh: true })}
            disabled={refreshing || loading}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-60"
          >
            <RefreshCcw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {loading && (
        <section className="rounded-lg border border-gray-100 bg-white p-6 text-sm text-gray-500">
          <InlineLoader label="Loading usage…" />
        </section>
      )}

      {error && !loading && (
        <section className="flex items-start gap-3 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </section>
      )}

      {/* A missing credential is a configuration state, not an error — say so, or an
          empty dashboard reads as "nobody uses the app". */}
      {!loading && data?.disabled && (
        <section className="flex items-start gap-3 rounded-lg border border-amber-100 bg-amber-50 p-4 text-sm text-amber-800">
          <Settings2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Usage analytics is not configured on this environment. Set{' '}
            <code className="rounded bg-amber-100 px-1">POSTHOG_PERSONAL_API_KEY</code> on the API to enable it.
          </span>
        </section>
      )}

      {!loading && data && !data.disabled && (
        <>
          {data.degraded?.length > 0 && (
            <section className="flex items-start gap-3 rounded-lg border border-amber-100 bg-amber-50 p-4 text-sm text-amber-800">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{degradedSentence(data.degraded)}</span>
            </section>
          )}

          {/* Said once, at the top, in dates: which days these figures are, and why the
              last two are not among them. Without this the page reads as "right now". */}
          <section className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            <span>
              {range
                ? <><span className="font-medium text-gray-800">Complete days only: {range}.</span>{' '}</>
                : <><span className="font-medium text-gray-800">Complete days only.</span>{' '}</>}
              Today and yesterday are left out on purpose — phones send their usage when they are next opened, so
              those days are still filling in and always look low. Anything that started in the last two days will not
              be here yet.
              {hasUnsettledDays && ' The day-by-day chart does draw them, as hollow dots.'}
            </span>
          </section>

          {/* ── 1 ─────────────────────────────────────────────────────────────── */}
          <Card
            title="Minutes in the app per working day"
            action="This is the headline. Ground staff are measured on the days they actually worked — a day with a shift logged on it — so leave and days off are not in the figure. If a team is well under the usual range, the work is being done on paper or not at all; ask the area manager what the team is using instead."
          >
            {/* Both panels' empty state is a measurement — "nobody reported a full day
                yet" — so a block that failed may not reach them. Same rule as every other
                card here; see unreadable(). */}
            {unreadable(data, 'perDayUse') ? (
              <Unavailable what="This card could not be read this time." />
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <GroupPanel
                  label="Ground staff"
                  group={groundUse}
                  note="Counted on WORKING days only — days this person started or ended a shift, answered a random attendance check or sent a task submission. Leave and days off are left out, so this is the time on a day they were actually at work."
                  onOpenPeople={() => setOpenDrill({ drill: 'perDayUse.people', params: { group: 'ground' } })}
                />
                <GroupPanel
                  label="Managers"
                  group={managerUse}
                  note="Counted on every day they opened the app: managers do not mark attendance, so there is no working day to cut on. A project has only a handful of managers, so this figure moves a lot when one person is on leave."
                  onOpenPeople={() => setOpenDrill({ drill: 'perDayUse.people', params: { group: 'manager' } })}
                />
              </div>
            )}

            {/* The third bucket. Kept visible and kept OUT of both figures above: these are
                people whose role the app never resolved, not managers. */}
            {unknownUse && n(unknownUse.people) > 0 && (
              <p className="mt-3 text-xs text-gray-500">
                A further{' '}
                <DrillNumber
                  onOpen={() => setOpenDrill({ drill: 'perDayUse.people', params: { group: 'unknown' } })}
                  label="See the people whose role could not be identified"
                >
                  {people(unknownUse.people)}
                </DrillNumber>{' '}
                used the app but their role could not be identified, so they are
                in neither figure above. They averaged {minutes(unknownUse.medianMinutes)} on a day they used it.
              </p>
            )}

            {data.managers && n(data.managers.people) > 0 && (
              <div className="mt-4 rounded-lg border border-gray-100 p-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  What managers spend that time on
                  {/* "who used the app", not "in this project": the report only sees a
                      manager who opened it, so one on leave is in neither this figure nor
                      the list behind it. The old wording claimed a roster this page has
                      never had. */}
                  <span className="ml-2 font-normal text-gray-500">
                    (
                    <DrillNumber
                      onOpen={() => setOpenDrill({ drill: 'managers.people', params: {} })}
                      label={`See the ${people(data.managers.people)} and what each of them spends the time on`}
                    >
                      {people(data.managers.people)}
                    </DrillNumber>{' '}
                    who used the app)
                  </span>
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Approvals take{' '}
                  <DrillNumber
                    onOpen={() => setOpenDrill({ drill: 'managers.people', params: {} })}
                    label="See each manager's own time in the approvals queue"
                  >
                    {minutes(data.managers.approvalsMinutes)}
                  </DrillNumber>{' '}
                  per manager {phrase}
                  {n(data.managers.approvalsSecondsPerVisit) > 0 && (
                    <> — about {Math.round(n(data.managers.approvalsSecondsPerVisit))} seconds every time they open it</>
                  )}
                  .
                  {/* Only printed when there IS a comparison. A missing or zero figure here
                      would otherwise read as "they never look at their team", which is an
                      accusation this page must not make from an absent number. */}
                  {n(data.managers.teamScreenMinutes) > 0 && (
                    <> Every screen about their own team put together takes{' '}
                      <DrillNumber
                        onOpen={() => setOpenDrill({ drill: 'managers.people', params: {} })}
                        label="See which managers look at their team, and which page they use"
                      >
                        {minutes(data.managers.teamScreenMinutes)}
                      </DrillNumber>.</>
                  )}
                </p>
                {n(data.managers.teamScreenMinutes) > 0
                  && n(data.managers.teamScreenMinutes) * 4 < n(data.managers.approvalsMinutes) && (
                  <p className="mt-2 text-xs text-gray-500">
                    Approvals are taking far more time than everything about the team put together — worth asking
                    whether sign-offs are being cleared without looking at the people behind them.
                  </p>
                )}
              </div>
            )}
          </Card>

          {/* ── 2 ─────────────────────────────────────────────────────────────── */}
          <Card
            title="Time in the app, day by day"
            action="Look for the step down. A drop on one day that stays down is a release or a network problem; a dip every Sunday is just the roster."
          >
            {unreadable(data, 'dayByDay') ? (
              <Unavailable what="This card could not be read this time." />
            ) : dayChart.rows.length === 0 ? (
              <Unavailable what="No usage reported for this project in the selected days." />
            ) : (
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  {/* Clicking anywhere on a day opens that day's people. Recharts reports the
                      day by POSITION, which is why the list is indexed rather than matched on
                      the printed label — two days in different months can print the same. */}
                  <LineChart
                    data={dayChart.rows}
                    margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
                    onClick={(e: any) => {
                      const row = dayChart.rows[Number(e?.activeTooltipIndex)];
                      if (row?.day) setOpenDrill({ drill: 'dayByDay.people', params: { day: String(row.day), group: dayChart.group } });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef0f3" />
                    <XAxis dataKey="dayLabel" tick={{ fontSize: 12, fill: '#6b7280' }} />
                    <YAxis unit=" min" tick={{ fontSize: 12, fill: '#6b7280' }} />
                    <Tooltip
                      formatter={(v, _name, p: any) =>
                        [
                          `${minutes(v)} each, across ${people(p?.payload?.people ?? 0)}`,
                          'Typical time per person',
                        ] as [string, string]
                      }
                      labelFormatter={(label, payload) =>
                        payload?.[0]?.payload?.unsettled ? `${label} — still filling in` : String(label)
                      }
                    />
                    <Line
                      type="monotone" dataKey="medianMinutes" name="Typical time per person"
                      stroke={SERIES} strokeWidth={2}
                      dot={(p: DotItemDotProps) => (
                        <circle
                          cx={p.cx} cy={p.cy} r={3} stroke={SERIES} strokeWidth={2}
                          fill={(p.payload as { unsettled?: boolean } | undefined)?.unsettled ? '#fff' : SERIES}
                        />
                      )}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            <p className="mt-3 text-xs text-gray-500">
              How long a typical member of the {GROUP_LABEL[dayChart.group]} spent in the app on each day. Hollow dots
              are days that are still filling in. Click a day to see who used the app that day.
            </p>
          </Card>

          {/* ── 3 ─────────────────────────────────────────────────────────────── */}
          <Card
            title="Where the hours go"
            action="Grey bars are people finding their way around, not doing work. If a part of the job you care about is near the bottom, the team is not reaching it — check where it sits in the app."
          >
            {unreadable(data, 'timeShare') ? (
              <Unavailable what="This card could not be read this time." />
            ) : areaChart.rows.length === 0 ? (
              <Unavailable what="Not enough reported time to split it by area yet." />
            ) : (
              <>
                <div className="h-96 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    {/* Clicking a bar opens the people whose hours those are. */}
                    <BarChart
                      data={areaChart.rows}
                      layout="vertical"
                      margin={{ top: 4, right: 48, bottom: 4, left: 8 }}
                      onClick={(e: any) => {
                        const row = areaChart.rows[Number(e?.activeTooltipIndex)];
                        if (row?.area) setOpenDrill({ drill: 'timeShare.area', params: { area: String(row.area), group: areaChart.group } });
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef0f3" horizontal={false} />
                      <XAxis type="number" unit="%" tick={{ fontSize: 12, fill: '#6b7280' }} />
                      <YAxis type="category" dataKey="area" width={170} tick={{ fontSize: 11, fill: '#374151' }} />
                      <Tooltip
                        formatter={(v, _name, p: any) =>
                          [
                            `${Number(v)}% of the time — ${Number(p?.payload?.hours ?? 0).toLocaleString()} hours${
                              n(p?.payload?.secondsPerVisit) > 0
                                ? `, about ${Math.round(n(p?.payload?.secondsPerVisit))} seconds per visit`
                                : ''
                            }`,
                            p?.payload?.navigation ? 'Finding their way around' : 'Share of time',
                          ] as [string, string]
                        }
                      />
                      <Bar dataKey="sharePct" name="Share of time" radius={[0, 4, 4, 0]}>
                        {areaChart.rows.map((row) => (
                          <Cell key={row.area} fill={row.navigation ? NEUTRAL : SERIES} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {navigationShare > 0 && (
                  <p className="mt-3 text-sm text-gray-600">
                    {Math.round(navigationShare)}% of all time is the home screen and the tab bar — moving between
                    things rather than doing them.
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-500">Click a bar to see whose hours those are.</p>
                {/* Unconditional: see the header note. Card 3 is the one card whose figures
                    are partly inferred, and it must say so every time it is shown. */}
                <p className="mt-2 text-xs text-gray-500">
                  {areaChart.group === 'ground'
                    ? 'Ground team only. '
                    : `Shown for the ${GROUP_LABEL[areaChart.group]}. `}
                  {measuredNote(data.timeShareMeasuredPct)}
                </p>
              </>
            )}
          </Card>

          {/* ── 12 (shown here, where it reads: the full list behind card 3) ──── */}
          <Card
            title="How long people spend on each page"
            action="The full list behind the card above. A long time on a page the team works in is where the effort goes; a long time on a page marked “Getting around” only means people are hunting for something. Home sits near the top on total hours and is not a feature anyone is using — read the work pages against each other, not against it."
          >
            {unreadable(data, 'pageTime') ? (
              <Unavailable what="This card could not be read this time." />
            ) : pageAreas.length === 0 ? (
              <Unavailable what="Not enough reported time to split it by page yet." />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-500">
                        <th className="py-2 pr-3 font-medium">Page</th>
                        <th className="py-2 pl-3 text-right font-medium">People</th>
                        <th className="py-2 pl-3 text-right font-medium">Visits</th>
                        <th className="py-2 pl-3 text-right font-medium">Total time</th>
                        <th className="py-2 pl-3 text-right font-medium">Typical visit</th>
                        <th className="py-2 pl-3 text-right font-medium">Typical per person</th>
                      </tr>
                    </thead>
                    {/* One tbody per area: the group heading is a row of its own, so the six
                        columns stay in one alignment down the whole card. */}
                    {pageAreas.map((group) => (
                      <tbody key={group.area}>
                        <tr className="border-b border-gray-100 bg-gray-50">
                          <th
                            colSpan={6}
                            scope="colgroup"
                            className="py-2 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                          >
                            {group.area}
                            <span className="ml-2 font-normal normal-case tracking-normal text-gray-500">
                              {hours(group.hours)} in total
                              {group.navigation && ' — getting around, not doing the job'}
                            </span>
                          </th>
                        </tr>
                        {group.pages.map((row, i) => (
                          <tr key={`${group.area}-${row.page}-${i}`} className="border-b border-gray-50 last:border-0">
                            <td className="py-2 pr-3">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-gray-900">{row.page}</span>
                                {/* Flagged ON THE ROW, not only in the subtitle: this card is
                                    read by scanning the top of it, and the top of it is Home. */}
                                {row.navigation && (
                                  <span className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                                    <Compass className="h-3 w-3" aria-hidden="true" />
                                    Getting around
                                  </span>
                                )}
                              </div>
                              {/* The bar is the hours, so the eye ranks the rows without
                                  reading them. A page with no measured time gets an empty
                                  track and no mark at all: a 2%-wide stub beside three em
                                  dashes says "a little time", which is the one thing those
                                  dashes are there to deny. */}
                              <div className="mt-1 h-1.5 w-full max-w-xs overflow-hidden rounded bg-gray-100">
                                {n(row.hours) > 0 && (
                                  <div
                                    className="h-full rounded"
                                    style={{
                                      width: `${Math.max(2, (n(row.hours) / pageTimeMax) * 100)}%`,
                                      backgroundColor: row.navigation ? NEUTRAL : SERIES,
                                    }}
                                  />
                                )}
                              </div>
                            </td>
                            <td className="py-2 pl-3 text-right align-top tabular-nums text-gray-700">
                              {/* A row whose screen name never arrived cannot be opened — the
                                  API would refuse the empty selector — so it stays plain text
                                  rather than offering a control that answers with an error. */}
                              {row.screen ? (
                                <DrillNumber
                                  onOpen={() => setOpenDrill({ drill: 'pageTime.people', params: { screen: String(row.screen) } })}
                                  label={`See who spends the time on ${row.page}`}
                                >
                                  {count(row.people)}
                                </DrillNumber>
                              ) : count(row.people)}
                            </td>
                            <td className="py-2 pl-3 text-right align-top tabular-nums text-gray-700">
                              {count(row.visits)}
                            </td>
                            {/* A page the phone never timed shows an em dash across all three
                                time columns, never "0 hours" or "0 seconds": a zero here is a
                                missing measurement — an older build that reports visits and
                                no durations — not a page people opened and spent no time on.
                                A span that is real but tiny floors instead; see hours(). */}
                            <td className="py-2 pl-3 text-right align-top tabular-nums text-gray-900">
                              {n(row.hours) > 0 ? hours(row.hours) : '—'}
                            </td>
                            <td className="py-2 pl-3 text-right align-top tabular-nums text-gray-600">
                              {n(row.medianSecondsPerVisit) > 0 ? seconds(row.medianSecondsPerVisit) : '—'}
                            </td>
                            <td className="py-2 pl-3 text-right align-top tabular-nums text-gray-600">
                              {n(row.medianMinutesPerPerson) > 0 ? minutes(row.medianMinutesPerPerson) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    ))}
                  </table>
                </div>
                <p className="mt-3 text-xs text-gray-500">
                  Ground team only — a handful of pages are shared with managers, so counting both would make it
                  impossible to say whose minutes these are. “Typical visit” is the middle visit and “typical per
                  person” the middle person {phrase}, so one heavy user cannot move either figure.{' '}
                  {measuredNote(data.timeShareMeasuredPct)}
                </p>
              </>
            )}
          </Card>

          {/* ── 4 ─────────────────────────────────────────────────────────────── */}
          <Card
            title="Opened it vs actually used it"
            action="A big gap means people land on the tab and leave. Either the content is not worth opening or they cannot find it — pick one area and ask five people which."
          >
            {unreadable(data, 'reach') ? (
              <Unavailable what="This card could not be read this time." />
            ) : reachRows.length === 0 ? (
              <Unavailable what="Not enough reported use to compare opening a tab with using it." />
            ) : (
              <div className="space-y-5">
                {reachRows.map((row) => {
                  const opened = n(row.openedPeople);
                  const did = n(row.didPeople);
                  const pct = opened > 0 ? (did / opened) * 100 : 0;
                  return (
                    <div key={row.area}>
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="text-sm font-medium text-gray-900">{row.area}</span>
                        <span className={`text-sm font-semibold tabular-nums ${pct < 50 ? 'text-red-700' : 'text-gray-700'}`}>
                          <DrillNumber
                            onOpen={() => setOpenDrill({ drill: 'reach.area', params: { area: row.area, which: 'did' } })}
                            // Its own words, not the line below's: two controls that open the
                            // same list must still say which number the reader clicked.
                            label={`See the ${people(did)} who went further`}
                          >
                            {Math.round(pct)}% went further
                          </DrillNumber>
                        </span>
                      </div>
                      {/* A bar inside a bar: the grey track is everyone who opened the tab,
                          the blue fill is everyone who did the real thing inside it. */}
                      <div className="mt-2 h-5 w-full overflow-hidden rounded bg-gray-200">
                        <div
                          className="h-full rounded"
                          style={{ width: `${Math.min(100, Math.max(pct, did > 0 ? 1 : 0))}%`, backgroundColor: SERIES }}
                        />
                      </div>
                      <div className="mt-1.5 text-xs text-gray-600">
                        {row.openedLabel}:{' '}
                        <DrillNumber
                          onOpen={() => setOpenDrill({ drill: 'reach.area', params: { area: row.area, which: 'opened' } })}
                          label={`See the ${people(opened)} who ${row.openedLabel}`}
                        >
                          {people(opened)}
                        </DrillNumber>
                        {n(row.openedMedianDays) > 0 && <>, on {days(row.openedMedianDays)} each</>}.{' '}
                        {row.didLabel}:{' '}
                        <DrillNumber
                          onOpen={() => setOpenDrill({ drill: 'reach.area', params: { area: row.area, which: 'did' } })}
                          label={`See the ${people(did)} who ${row.didLabel}`}
                        >
                          {people(did)}
                        </DrillNumber>
                        {n(row.didMedianDays) > 0 && <>, on {days(row.didMedianDays)} each</>}.
                        {/* The gap gets its OWN control and its own words. Opening it from the
                            "% went further" figure would answer a good-news number with the
                            list of everyone it does not describe. */}
                        {opened > did && (
                          <>
                            {' '}
                            <DrillNumber
                              onOpen={() => setOpenDrill({ drill: 'reach.area', params: { area: row.area, which: 'gap' } })}
                              label={`See the ${people(opened - did)} who went in and never ${row.didLabel}`}
                            >
                              {people(opened - did)}
                            </DrillNumber>{' '}
                            went in and never did.
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* ── 5 ── "What stopped people" — BUILT, SHOWN TO NOBODY ───────────────
              Taken off the page on 26 Oct 2026. This page answers how the app is USED;
              failure data answers a different question for an audience the owner wants to
              choose deliberately. Hidden from everyone, super admins included.
              The API hides it too (HIDDEN_BLOCKS in posthog-usage.service.ts), so the
              queries do not run and `blockers` arrives empty — flip BOTH to bring it back.
              Nothing here is deleted: the card below still works as written. */}
          {SHOW_WHAT_STOPPED_PEOPLE && (
          <Card
            title={`What stopped people ${phrase}`}
            action="Work the top row of each table first, but read the recovery column: something that stopped 500 people who all finished anyway is wasted time, not lost work. Open a row to see exactly who it happened to."
          >
            {unreadable(data, 'blockers') ? (
              <Unavailable what="This card could not be read this time." />
            ) : !anyBlockers ? (
              <Unavailable what="Nothing blocked anyone in this window." />
            ) : (
              <div className="space-y-6">
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-gray-900">Things that went wrong</h3>
                  {errorBlockers.length === 0 ? (
                    <p className="text-sm text-gray-500">Nothing went wrong in this window.</p>
                  ) : (
                    <BlockerTable rows={errorBlockers} tone="error" />
                  )}
                </div>
                {/* Its own group: nothing in the app can fix a permission the person turned
                    off, and the fix is a conversation, not a bug report. */}
                {settingBlockers.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Phone settings that need fixing</h3>
                    <p className="mb-2 text-xs text-gray-500">
                      The app asked for the camera or the location and the phone said no. Nothing can be fixed from
                      here — somebody has to walk the person through their phone settings once.
                    </p>
                    <BlockerTable rows={settingBlockers} tone="setting" />
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Rules that said no</h3>
                  <p className="mb-2 text-xs text-gray-500">
                    These are the app working as designed — out of the allowed area, outside the allowed time, already
                    done. They only need attention if the same person hits one every day.
                  </p>
                  {policyBlockers.length === 0 ? (
                    <p className="text-sm text-gray-500">No rule stopped anyone in this window.</p>
                  ) : (
                    <BlockerTable rows={policyBlockers} tone="policy" />
                  )}
                </div>
              </div>
            )}
          </Card>
          )}

          {SHOW_PEOPLE_WORKLISTS && (<>
          {/* ── 7 ─────────────────────────────────────────────────────────────── */}
          <Card
            title="Marking attendance some other way"
            action="Only the rows that say nothing in the app need a call. A market-attendance request is the normal route for someone working away from a store, and 1Office already has that day as Present."
          >
            {!noAttendance ? (
              <Unavailable what="This list could not be read this time." />
            ) : (
              <>
                <p className="mb-3 text-sm text-gray-600">
                  {people(noAttendance.people)} opened the app on{' '}
                  {minActiveDays > 0 ? `at least ${days(minActiveDays)}` : 'most days'} {phrase} and never used the
                  app's own check-in. That does not mean they were absent: their attendance reaches 1Office another
                  way, and the last column says which.
                </p>
                {marketRouteCount > 0 && (
                  <p className="mb-3 text-sm text-gray-600">
                    <strong>{marketRouteCount}</strong>{' '}
                    {marketRouteCount === 1 ? 'of them raises' : 'of them raise'} a market-attendance request instead
                    — the route for a day spent out of the store. It is sent from the phone straight to 1Office, which
                    records the day as an approved market regularization, so the app never logs a check-in for them.
                    Nothing is wrong with these.
                  </p>
                )}
                {otherRouteCount > 0 && (
                  <p className="mb-3 text-sm text-gray-600">
                    <strong>{otherRouteCount}</strong> {otherRouteCount === 1 ? 'has' : 'have'} nothing in the app at
                    all — no check-in and no market request — so whatever marks their day happens outside it. These
                    are the {otherRouteCount === 1 ? 'row' : 'rows'} worth a call.
                  </p>
                )}
                <HiddenLeavers count={noAttendance.hiddenLeavers} />
                <WorklistTable<WorklistRow>
                  people={noAttendance.rows ?? []}
                  total={n(noAttendance.people)}
                  emptyMessage="Everyone who used the app also checked in from it."
                  columns={[
                    { header: 'Days on the app', cell: (p) => days(p.activeDays) },
                    { header: 'Time in the app', cell: (p) => minutes(p.minutes) },
                    { header: 'How their day is marked', cell: (p) => dayMarkingLabel(p.dayMarking) },
                  ]}
                />
              </>
            )}
          </Card>

          {/* ── 8 ─────────────────────────────────────────────────────────────── */}
          <Card
            title="Barely using the app"
            action="Start with the shortest times. A phone that cannot run the app, a person who never got shown it, and a person who has quietly stopped all look the same here — one call tells them apart."
          >
            {!barely ? (
              <Unavailable what="This list could not be read this time." />
            ) : (
              <>
                <p className="mb-3 text-sm text-gray-600">
                  {people(barely.people)} spent{' '}
                  {barelyThreshold > 0 ? `under ${barelyThreshold} min in total` : 'almost no time in the app'} {phrase}.
                </p>
                {/* The list this note was written for: before the API hid them, the top of
                    this table was people who had left the company months ago. */}
                <HiddenLeavers count={barely.hiddenLeavers} />
                <WorklistTable<WorklistRow>
                  people={barely.rows ?? []}
                  total={n(barely.people)}
                  emptyMessage="Nobody is below the cut-off for this window."
                  columns={[
                    { header: 'Total time', cell: (p) => minutes(p.minutes) },
                    { header: 'Days opened', cell: (p) => days(p.activeDays) },
                    { header: 'Days checked in', cell: (p) => days(p.checkIns) },
                  ]}
                />
              </>
            )}
          </Card>
          </>)}

          {/* ── 9 ─────────────────────────────────────────────────────────────── */}
          <Card
            title="When the day actually happens"
            action={(
              <>
                {hourCardAction(data.taskTiming)}
                {/* The sentence above counts DAYS; these open the PEOPLE behind it, which
                    is a different number and says so in the window. Worded rather than
                    numbered for exactly that reason — a control labelled with the day count
                    would promise a list of that many people. */}
                {data.taskTiming && (
                  <span className="mt-1 block">
                    See the people:{' '}
                    <DrillNumber
                      onOpen={() => setOpenDrill({ drill: 'taskTiming.tasks', params: { which: 'several' } })}
                      label="See the people who log several task submissions on one day"
                    >
                      several on one day
                    </DrillNumber>
                    {' · '}
                    <DrillNumber
                      onOpen={() => setOpenDrill({ drill: 'taskTiming.tasks', params: { which: 'together' } })}
                      label="See the people who send a day's task submissions in one sitting"
                    >
                      sent in one sitting
                    </DrillNumber>
                    {' · '}
                    <DrillNumber
                      onOpen={() => setOpenDrill({ drill: 'taskTiming.tasks', params: { which: 'neverOut' } })}
                      label="See the people who report on a day they never closed"
                    >
                      reported on a day with no check-out
                    </DrillNumber>
                  </span>
                )}
              </>
            )}
          >
            {/* Two different answers, and they must not be swapped: a block that failed
                says so, and only a block that ARRIVED empty may say nothing was logged.
                Both halves of this card come from the one `dayShape` block, so that key
                covers the hours and the task-logging figures alike. */}
            {dayShapeUnreadable ? (
              <Unavailable what="This card could not be read this time." />
            ) : hourPanels.length === 0 ? (
              <Unavailable what="Nothing was logged against the hours of the day in this window." />
            ) : (
              <>
                <div className="grid gap-6 md:grid-cols-2">
                  {hourPanels.map((panel) => (
                    <div key={panel.key}>
                      <h3 className="text-sm font-semibold text-gray-900">{panel.activity}</h3>
                      <p className="mt-0.5 text-xs text-gray-500">
                        Logged {times(panel.records, TIMES)} by{' '}
                        {panel.action ? (
                          <DrillNumber
                            onOpen={() => setOpenDrill({ drill: 'dayShape.activity', params: { activity: panel.action } })}
                            label={`See who logged ${panel.activity}`}
                          >
                            {people(panel.people)}
                          </DrillNumber>
                        ) : people(panel.people)}{' '}
                        {phrase}, busiest at {clock(panel.busiestHour)}.
                      </p>
                      <div className="mt-2 h-40 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          {/* Clicking an hour opens the people who logged it in THAT hour —
                              the question a spike in the bar chart actually raises. */}
                          <BarChart
                            data={panel.series}
                            margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
                            onClick={(e: any) => {
                              const bar = panel.series[Number(e?.activeTooltipIndex)];
                              if (panel.action && bar) {
                                setOpenDrill({ drill: 'dayShape.activity', params: { activity: panel.action, hour: String(bar.hour) } });
                              }
                            }}
                            style={panel.action ? { cursor: 'pointer' } : undefined}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef0f3" vertical={false} />
                            {/* Every third hour is labelled: a tick on all eighteen hours
                                is unreadable at this size, and what the reader needs is
                                the clock, not the count of hours. */}
                            <XAxis
                              dataKey="hour" interval={0} tickLine={false}
                              tickFormatter={(h) => (Number(h) % 3 === 0 ? clock(Number(h)) : '')}
                              tick={{ fontSize: 11, fill: '#6b7280' }}
                            />
                            <YAxis
                              width={44} tick={{ fontSize: 11, fill: '#6b7280' }}
                              tickFormatter={(v) => Number(v).toLocaleString()}
                            />
                            <Tooltip
                              formatter={(v) => [times(v, TIMES), 'Logged in this hour'] as [string, string]}
                              labelFormatter={(h) => `${clock(Number(h))} to ${clock(Number(h) + 1)}`}
                            />
                            <Bar dataKey="records" name="Logged in this hour" fill={SERIES} radius={[2, 2, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      {panel.band && (
                        <p className="mt-1 text-xs text-gray-600">
                          {percent(panel.bandPct)} of them between {panel.band}.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-gray-500">
                  Ground team only, on IST hours. Each bar counts the times that job was finished in that hour of the
                  day, added up across the days above — so this is when the work gets recorded, not how busy the shop
                  floor was.
                </p>
              </>
            )}
          </Card>

          {/* ── 10 ────────────────────────────────────────────────────────────── */}
          <Card
            title="Training: started, finished, given up"
            action="Totals for each kind of course over the same days, not one course followed through — nothing in the app says which course a start and a finish belong to, so read each kind on its own rather than against the other. A kind where little is finished is worth a look at the content itself: how long it is, and whether it can be finished in the few minutes people have on the floor."
          >
            {/* Same split as card 9. The service always returns a row per kind when the
                query ran, so an empty `training` with nothing in `degraded` really does
                mean nobody opened a course. */}
            {trainingUnreadable ? (
              <Unavailable what="This card could not be read this time." />
            ) : trainingRows.length === 0 ? (
              <Unavailable what="Nobody started a course in this window." />
            ) : (
              <div className="space-y-6">
                {trainingRows.map((row) => {
                  const started = n(row.started);
                  const finished = n(row.finished);
                  const gaveUp = n(row.gaveUp);
                  const failed = n(row.finishFailed);
                  return (
                    <div key={row.kind}>
                      <h3 className="text-sm font-semibold text-gray-900">{row.kind}</h3>
                      <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Started</div>
                          <div className="mt-1 text-2xl font-bold tabular-nums text-gray-900">
                            {row.kindKey ? (
                              <DrillNumber
                                onOpen={() => setOpenDrill({ drill: 'training.kind', params: { kind: String(row.kindKey), metric: 'started' } })}
                                label={`See who opened ${row.kind}`}
                              >
                                {Math.round(started).toLocaleString()}
                              </DrillNumber>
                            ) : Math.round(started).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-600">
                            courses opened by {people(row.people)}
                            {n(row.couldNotOpen) > 0 && (
                              <>, and {Math.round(n(row.couldNotOpen)).toLocaleString()} more would not open at all</>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Finished</div>
                          <div className="mt-1 text-2xl font-bold tabular-nums text-gray-900">
                            {row.kindKey && finished > 0 ? (
                              <DrillNumber
                                onOpen={() => setOpenDrill({ drill: 'training.kind', params: { kind: String(row.kindKey), metric: 'finished' } })}
                                label={`See who finished ${row.kind}`}
                              >
                                {Math.round(finished).toLocaleString()}
                              </DrillNumber>
                            ) : Math.round(finished).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-600">
                            {n(row.finishedPeople) > 0
                              ? <>finishes recorded, by {people(row.finishedPeople)}</>
                              : <>finishes recorded</>}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Given up</div>
                          {/* Red only when more were left than finished: a give-up count
                              on its own is ordinary, and colouring it every time would cry
                              wolf on every kind of course. */}
                          <div
                            className={`mt-1 text-2xl font-bold tabular-nums ${gaveUp > finished ? '' : 'text-gray-900'}`}
                            style={gaveUp > finished ? { color: ALERT } : undefined}
                          >
                            {/* NOT CLICKABLE when the finish may never be reported back.
                                The card itself says this kind of content looks like content
                                whose finish is not recorded — naming the people under that
                                heading would hand a program team a list of names for a
                                fault in the player. The figure stays; the list does not. */}
                            {row.kindKey && !row.finishMayNotBeReported ? (
                              <DrillNumber
                                onOpen={() => setOpenDrill({ drill: 'training.kind', params: { kind: String(row.kindKey), metric: 'gaveUp' } })}
                                label={`See who left ${row.kind} without a finish`}
                              >
                                {Math.round(gaveUp).toLocaleString()}
                              </DrillNumber>
                            ) : Math.round(gaveUp).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-600">
                            left without a recorded finish
                            {n(row.gaveUpMedianSeconds) > 0 && (
                              // Open time, not effort: the API's comment is explicit that
                              // it includes reading and idle time, so it is worded as how
                              // long the course was open and nothing more.
                              <>, open {seconds(row.gaveUpMedianSeconds)} first</>
                            )}
                          </div>
                        </div>
                        {/* WORDED AS WHAT THE API MEASURED, which is not a software fault.
                            For the in-app lessons this count is the SERVER declining the
                            submission — the module was not passed, or it had already been
                            sent — so the old heading "Would not complete / the course
                            reported it could not finish" sent a program manager to
                            engineering over 303 people not passing a quiz. The reverse of
                            the mistake the amber note below exists to prevent. And a zero
                            is worded as nothing having come back, never as nobody failing:
                            one of these two kinds has no failure path at all, so its zero
                            is a property of the code and not news about the learners. */}
                        <div>
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Not passed
                          </div>
                          <div className="mt-1 text-2xl font-bold tabular-nums text-gray-900">
                            {Math.round(failed).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-600">
                            {failed === 0
                              ? 'nothing came back as not passed, which some kinds of course cannot report at all'
                              : 'the server did not pass the submission, or it had already been sent'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {/* Said once, under both halves: the two kinds are measured by different
                    code, so the columns do not mean the same thing across the rows. A
                    reader comparing give-ups between them is comparing two pieces of
                    software, not two sets of people. */}
                {trainingRows.length > 1 && (
                  <p className="text-xs text-gray-500">
                    Do not read the two kinds against each other. They are measured by different parts of the app: the
                    course player can only report a finish if it volunteers one, and has no way to report a failure,
                    while the in-app lessons are graded by the server and have no way to report a give-up. Each kind
                    only compares with itself.
                  </p>
                )}
              </div>
            )}
          </Card>

          {SHOW_PEOPLE_WORKLISTS && (<>
          {/* ── 11 ────────────────────────────────────────────────────────────── */}
          <Card
            title="Never sees the app's notifications"
            action="Opening none is not proof that nothing was ever sent — nothing in the app records a message arriving, and plenty of people see the badge and go to the screen by hand. Treat it as a list of handsets whose notification switch is worth checking: it costs a minute, and random attendance is the one message where missing it costs the person their day."
          >
            {!noNotifications ? (
              <Unavailable what="This list could not be read this time." />
            ) : (
              <>
                <p className="mb-3 text-sm text-gray-600">
                  {people(noNotifications.people)} were on the app on{' '}
                  {notificationsMinDays > 0 ? `at least ${days(notificationsMinDays)}` : 'most days'} {phrase} and
                  never opened a single notification
                  {medianOpens > 0 && (
                    <>, while a typical person in this project opened {times(medianOpens, NOTIFICATIONS)}</>
                  )}
                  .
                </p>
                {/* The measured reason this list is not an accusation. Printed whenever
                    the API sends it: most of these people demonstrably reach the prompt
                    another way, and a card that left that out would read as "these people
                    missed their checks" — which the figures do not say. */}
                {stillAnsweredChecks > 0 && (
                  <p className="mb-3 text-sm text-gray-600">
                    {Math.round(stillAnsweredChecks).toLocaleString()} of these{' '}
                    {people(noNotifications.people)} still answered at least one surprise check {phrase}, so as a
                    group they are getting to it some other way. This is a list of settings to check, not of people
                    who missed their checks.
                  </p>
                )}
                <HiddenLeavers count={noNotifications.hiddenLeavers} />
                <WorklistTable<WorklistRow>
                  people={noNotifications.rows ?? []}
                  total={n(noNotifications.people)}
                  emptyMessage="Everyone who was on the app all week opened at least one notification."
                  columns={[
                    { header: 'Days on the app', cell: (p) => days(p.activeDays) },
                    { header: 'Time in the app', cell: (p) => (p.minutes == null ? '—' : minutes(p.minutes)) },
                  ]}
                />
              </>
            )}
          </Card>
          </>)}

          <p className="text-xs leading-relaxed text-gray-500">
            Everything here covers signed-in staff on this project only, on the live app. The app reports a short,
            fixed list of things — the screens people reached and the jobs they finished or failed — so it can say
            where the time went and what got done, not what someone typed or saw. Most of the time shown is counted
            by the phone while the app was open on screen; for older phones still in the field the rest is worked out
            from when the app next reported, and the card that mixes the two says so. Days run on IST, and the two
            most recent days are left out because they are still filling in.
          </p>
        </>
      )}

      {/* One window for every openable number on the page. It is mounted once, outside the
          cards, so opening a list never re-renders the card it came from — and closing it
          leaves the page exactly as the reader left it, scrolled where they were. */}
      <MobileUsagePeopleModal
        projectId={selectedProjectId ?? null}
        days={covered}
        request={openDrill}
        onClose={() => setOpenDrill(null)}
      />
    </div>
  );
}

/**
 * Did one person, on the days this blocker hit them, still finish the job?
 *
 * The API answers this as a boolean — true only when EVERY one of those days ended with the
 * job done — and this prints it as the two answers a program manager acts on. A missing
 * verdict is an em dash: "not every day" and "we were not told" are different, and only one
 * of them is a reason to ring somebody.
 */
const stillGotItDone = (p: BlockerPerson): string => {
  if (p.gotItDone == null) return '—';
  return p.gotItDone ? 'Every time' : 'Not every day';
};

/**
 * The tables inside "What stopped people": same columns, three tones, so the reader never
 * mistakes a rule that said no for something broken — or for a switch on a phone.
 *
 * A ROW OPENS INTO THE PEOPLE IT HAPPENED TO. "410 people lost their connection" is not
 * something anyone can act on; four names and their stores is. The ids come down with the
 * row and are named by the same identity path as the follow-up lists, so opening a row
 * costs nothing — no second request, no spinner.
 *
 * ONLY A ROW THAT HAS PEOPLE IS A CONTROL. The API caps the people per row and hides the
 * ones who have left, so a row can legitimately arrive with a big count and no names at
 * all. Such a row is rendered as plain text: no pointer, no chevron, no focus stop. A
 * control that opens nothing teaches the reader that the card is broken.
 *
 * One row open at a time per table, by design: a table is read by comparing the open row's
 * people against the counts above them, and a card with five panels unfolded is a wall. The
 * three tables keep their own open row because they are three different responses — a rule
 * that said no is not read against something broken.
 */
const BlockerTable = ({ rows, tone }: { rows: BlockerRow[]; tone: 'error' | 'setting' | 'policy' }) => {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const max = Math.max(...rows.map((r) => n(r.people)), 1);
  const barColour = tone === 'error' ? ALERT : tone === 'setting' ? WARN : NEUTRAL;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-500">
            <th className="py-2 pr-3 font-medium">What happened</th>
            <th className="py-2 pr-3 text-right font-medium">People</th>
            <th className="py-2 pr-3 text-right font-medium">Times</th>
            <th className="py-2 pr-3 text-right font-medium">Still got it done</th>
            <th className="py-2 text-right font-medium">Never got it done</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, rowIndex) => {
            // Recovery is per person-DAY, not per person: the same person can be stopped on
            // Monday, finish anyway, and be stopped again on Friday. Dividing by people
            // would quietly overstate it.
            const recovered = share(r.recoveredAttemptDays, r.attemptDays);
            const key = `${r.kind}-${r.what}`;
            const panelId = `blocker-people-${tone}-${rowIndex}`;
            // The rows are untrusted input like everything else in the payload: a non-array
            // must leave the row closed rather than throw inside the render.
            const named: BlockerPerson[] = Array.isArray(r.who?.rows) ? (r.who?.rows ?? []) : [];
            const canOpen = named.length > 0;
            // The API names at most the worst 200 and drops the leavers, so the panel can be
            // a strict subset of the row's own count. When it is, say so IN THE ROW'S OWN
            // number and leave the arithmetic alone: two names must not be read as the whole
            // of a failure that hit four hundred people, and "the 4 we looked up" is a
            // reconciliation nobody asked for. How much of the panel is on screen, and who
            // was dropped from it, are the table's own two lines.
            const isSubset = n(r.people)
              > Math.max(n(r.who?.people), named.length) + Math.round(n(r.who?.hiddenLeavers));
            const open = canOpen && openKey === key;
            const toggle = () => setOpenKey(open ? null : key);
            return (
              <Fragment key={key}>
                <tr
                  className={`border-b border-gray-50 ${open ? 'bg-gray-50' : ''} ${
                    canOpen ? 'cursor-pointer hover:bg-gray-50' : ''
                  }`}
                  onClick={canOpen ? toggle : undefined}
                >
                  <td className="py-2 pr-3">
                    {canOpen ? (
                      // A real button, so the row is reachable and operable from the
                      // keyboard; the click on the <tr> is a convenience on top of it and
                      // must not fire the toggle twice.
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggle(); }}
                        aria-expanded={open}
                        aria-controls={panelId}
                        className="flex w-full items-start gap-1.5 rounded text-left text-gray-900 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                      >
                        {open
                          ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                          : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />}
                        {/* The label is an element of its own, with the hint beside it rather
                            than inside it: the two must not merge into one string, or the
                            row stops being findable by the words the reader sees. */}
                        <span className="flex flex-wrap items-baseline gap-x-2">
                          <span>{r.what}</span>
                          <span className="whitespace-nowrap text-xs font-medium text-gray-500">
                            {open ? 'Hide who' : 'See who'}
                          </span>
                        </span>
                      </button>
                    ) : (
                      <div className="text-gray-900">{r.what}</div>
                    )}
                    {/* The bar is the people count, so the eye ranks the rows without reading. */}
                    {/* Indented to sit under the label rather than under the chevron, so the
                        bars of an expandable table still line up with each other. */}
                    <div className={canOpen ? 'pl-5' : ''}>
                      <div className="mt-1 h-1.5 w-full max-w-xs overflow-hidden rounded bg-gray-100">
                        <div
                          className="h-full rounded"
                          style={{ width: `${Math.max(2, (n(r.people) / max) * 100)}%`, backgroundColor: barColour }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-right align-top tabular-nums text-gray-900">
                    {n(r.people).toLocaleString()}
                  </td>
                  <td className="py-2 pr-3 text-right align-top tabular-nums text-gray-600">
                    {n(r.events) > 0 ? n(r.events).toLocaleString() : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right align-top tabular-nums text-gray-600">
                    {recovered ? `${recovered} of those days` : '—'}
                  </td>
                  <td className="py-2 text-right align-top tabular-nums text-gray-600">
                    {r.stuckPeople == null ? '—' : people(r.stuckPeople)}
                  </td>
                </tr>
                {open && (
                  <tr id={panelId} className="border-b border-gray-50 bg-gray-50">
                    <td colSpan={5} className="px-3 pb-4 pt-1">
                      {/* The column above says "still got it done" as a share of the DAYS
                          this went wrong on; the one in here answers it per person, as a
                          yes or a no. Two columns reading the same would be a trap, so this
                          one is named differently and the difference is said out loud. */}
                      <p className="mb-2 text-xs text-gray-600">
                        Who this happened to, the most affected first. “Got it done anyway” is about that person’s own
                        days: “Every time” is somebody who only lost time, and “Not every day” is the call to make.
                        {isSubset && <> This hit {people(r.people)} in all, and the worst of them are named here.</>}
                      </p>
                      <HiddenLeavers count={r.who?.hiddenLeavers} className="mb-2" />
                      <WorklistTable<BlockerPerson>
                        people={named}
                        total={Math.max(n(r.who?.people), named.length)}
                        emptyMessage="Nobody could be named for this one."
                        columns={[
                          { header: 'Days it hit them', cell: (p) => days(p.daysHit) },
                          { header: 'Got it done anyway', cell: (p) => stillGotItDone(p) },
                        ]}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MobileUsageReportPage;
